import { useEffect, useMemo, useState } from 'react'
import { Card, Tag, Text, Icon } from '@nimbus-ds/components'
import { ChevronDownIcon, ChevronRightIcon } from '@nimbus-ds/icons'
import { funnelService, type ProductsOriginsTimelineResponse } from '@/api/funnel'
import { goalService, merchantStatus, type Goal, type Actionable } from '@/api/goals'
import { authService } from '@/api/auth'
import { TrafficSourceTable } from '@/components/analytics/TrafficSourceTable'
import { TrafficSourceTimeline } from '@/components/analytics/TrafficSourceTimeline'
import { BreakdownTable } from '@/components/analytics/BreakdownTable'
import { VisitorSegmentation, type VisitorSegments } from '@/components/analytics/VisitorSegmentation'
import { RevenueAnalyticsCard, type RevenueAnalyticsInput } from '@/components/analytics/RevenueAnalyticsCard'
import { OffStorefrontCard, type OffStorefrontOrders } from '@/components/analytics/OffStorefrontCard'
import { ProductPerformanceTable } from '@/components/analytics/ProductPerformanceTable'

// ─────────────────────────────────────────────────────────────────────────────
// FunnelControlCenter — the funnel-as-spine CONTROL CENTER (prototype).
//
// One adaptive surface. The funnel's phases are the layout; every metric, every
// diagnosis, every action lives under the phase it belongs to. Density adapts by
// role: a merchant lands on collapsed phase HEADLINES (the digest); a consultant
// gets the panels expanded. Either can independently expand/collapse any phase —
// the simplification is organization, not subtraction.
//
// ADQUISICIÓN is built deep first as the reference pattern (metrics + diagnosis +
// actions + questions). The other four phases render their headline + the existing
// component for their body, with the deep diagnosis/actions treatment marked
// "próximamente" so the slot is visible, not faked.
// ─────────────────────────────────────────────────────────────────────────────

// ── shared shapes (the store funnel response is `any`) ──────────────────────
interface StepAnalysis {
  step_name: string
  step_order: number
  step_type?: string
  is_conversion?: boolean
  total_sessions: number
}
interface TnxAnalytics {
  step_analysis?: Record<string, StepAnalysis>
  total_conversions?: number
  visitor_segments?: VisitorSegments | null
  off_storefront_orders?: OffStorefrontOrders | null
  storefront_summary?: any
  product_analytics?: { product_performance?: { total_product_revenue?: number; top_products?: any } }
}

// ── small label maps (kept minimal; full taxonomy lives in the table/timeline) ─
const CHANNEL_ES: Record<string, string> = {
  'Paid Search': 'Búsqueda pagada', 'Organic Search': 'Búsqueda orgánica',
  'Paid Social': 'Social pago', 'Organic Social': 'Social orgánico',
  Direct: 'Directo', Referral: 'Referido', Email: 'Email', Display: 'Display',
  unattributed: 'Sin atribuir',
}
const chLabel = (k: string) => CHANNEL_ES[k] || k

// ── timeline window-aggregation (mirrors FunnelAnalytics.tsx:226-259) ─────────
function aggregateTimeline(t: ProductsOriginsTimelineResponse | null) {
  const channels: Record<string, { sessions: number; conversions: number; sessions_with_origin: number }> = {}
  const devices: Record<string, { sessions: number; conversions: number; revenue: number }> = {}
  const coverage = { sessions_total: 0, sessions_with_origin: 0 }
  for (const point of t?.series || []) {
    for (const [dev, cell] of Object.entries(point.devices || {})) {
      const d = (devices[dev] ||= { sessions: 0, conversions: 0, revenue: 0 })
      d.sessions += cell?.sessions || 0
      d.conversions += cell?.conversions || 0
      d.revenue += cell?.revenue || 0
    }
    for (const [ch, cell] of Object.entries(point.channels || {})) {
      const c = (channels[ch] ||= { sessions: 0, conversions: 0, sessions_with_origin: 0 })
      c.sessions += cell?.sessions || 0
      c.conversions += cell?.conversions || 0
      c.sessions_with_origin += cell?.sessions_with_origin || 0
    }
    const cov = point.origins_coverage
    if (cov) {
      coverage.sessions_total += cov.sessions_total || 0
      coverage.sessions_with_origin += cov.sessions_with_origin || 0
    }
  }
  return { channels, devices, coverage }
}

// Hot/cold channel from CR dispersion vs the store average (excludes unattributed).
function channelExtremes(channels: Record<string, { sessions: number; conversions: number }>) {
  const rows = Object.entries(channels)
    .filter(([k]) => k !== 'unattributed')
    .map(([key, c]) => ({ key, ...c, cr: c.sessions > 0 ? c.conversions / c.sessions : 0 }))
    .filter((r) => r.sessions > 0)
  const totalS = rows.reduce((a, r) => a + r.sessions, 0)
  const totalC = rows.reduce((a, r) => a + r.conversions, 0)
  const avgCR = totalS > 0 ? totalC / totalS : 0
  const cold = rows
    .filter((r) => r.sessions >= 0.1 * totalS && avgCR > 0 && r.cr < avgCR * 0.6)
    .sort((a, b) => b.sessions - a.sessions)[0]
  const hot = rows.filter((r) => avgCR > 0 && r.cr > avgCR * 1.4).sort((a, b) => b.cr - a.cr)[0]
  return { hot, cold, avgCR }
}

// Actionables a consultant bound to acquisition (traffic/origin/device indicators).
function acquisitionActions(goals: Goal[]): Actionable[] {
  const out: Actionable[] = []
  for (const g of goals) {
    for (const a of g.actionables || []) {
      if (a.status !== 'active' && a.status !== 'proposed') continue
      const traffic = a.indicators?.some(
        (i) => i.segment?.dimension === 'origin' || i.segment?.dimension === 'device' || i.binding?.kind === 'store_funnel'
      )
      if (traffic) out.push(a)
    }
  }
  return out
}

// ── presentational shell: a collapsible phase panel ──────────────────────────
type Diagnosis = { glyph: string; text: string; tone: 'hot' | 'cold' | 'mix' | 'neutral' } | null
const TONE: Record<string, string> = {
  hot: 'text-green-700 bg-green-50 border-green-200',
  cold: 'text-amber-700 bg-amber-50 border-amber-200',
  mix: 'text-blue-700 bg-blue-50 border-blue-200',
  neutral: 'text-muted-foreground bg-muted border-transparent',
}

function PhasePanel({
  name,
  chips,
  diagnosis,
  defaultOpen,
  children,
}: {
  name: string
  chips: string[]
  diagnosis?: Diagnosis
  defaultOpen: boolean
  children?: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Card>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start gap-3 p-4 text-left hover:bg-muted/40"
      >
        {open ? <Icon source={<ChevronDownIcon />} color="neutral-textLow" className="mt-0.5 shrink-0" /> : <Icon source={<ChevronRightIcon />} color="neutral-textLow" className="mt-0.5 shrink-0" />}
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <Text as="span" fontSize="caption" fontWeight="bold" className="uppercase tracking-wide">{name}</Text>
            {chips.map((c, i) => (
              <Text key={i} as="span" className="tabular-nums">{c}</Text>
            ))}
          </div>
          {diagnosis && (
            <div className={`inline-block rounded border px-2 py-0.5 text-xs ${TONE[diagnosis.tone]}`}>
              {diagnosis.glyph} {diagnosis.text}
            </div>
          )}
        </div>
      </button>
      {open && children && <Card.Body><div className="space-y-4 border-t pt-4">{children}</div></Card.Body>}
    </Card>
  )
}

// A marked-but-unbuilt slot, so the action/question split is visible, not faked.
function ComingSoon({ what }: { what: string }) {
  return (
    <Text fontSize="caption" className="rounded-md border border-dashed px-3 py-2">
      Próximamente: {what}
    </Text>
  )
}

function ActionsAndQuestions({ actions }: { actions: Actionable[] }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <Text fontSize="caption" fontWeight="bold" className="mb-1">Acciones</Text>
        {actions.length > 0 ? (
          <ul className="space-y-1">
            {actions.map((a) => (
              <li key={a.actionable_id} className="flex items-center gap-2 text-sm">
                <Tag appearance={a.status === 'active' ? 'primary' : 'neutral'}>
                  {a.status === 'active' ? 'activa' : 'propuesta'}
                </Tag>
                <Text as="span" color="neutral-textHigh" className="truncate">{a.name}</Text>
              </li>
            ))}
          </ul>
        ) : (
          <Text fontSize="caption">Sin acciones todavía — tu consultor puede sumar una acá.</Text>
        )}
      </div>
      <div>
        <Text fontSize="caption" fontWeight="bold" className="mb-1">Preguntas</Text>
        <ComingSoon what="investigaciones de descubrimiento ancladas a esta fase (Question Board)" />
      </div>
    </div>
  )
}

// ── main ─────────────────────────────────────────────────────────────────────
export function FunnelControlCenter() {
  const isMerchant = authService.isMerchant()
  const [tnx, setTnx] = useState<TnxAnalytics | null>(null)
  // The reader's window channel fold (`aggregateChannelsWindow`) — same numbers as the
  // timeline hand-sum below plus engaged/shoppers/engagement_sessions, which the table needs
  // for its engagement column. Kept separate from `tnx` because it sits at payload root.
  const [channelsWindow, setChannelsWindow] = useState<Record<string, any> | null>(null)
  const [timeline, setTimeline] = useState<ProductsOriginsTimelineResponse | null>(null)
  const [goals, setGoals] = useState<Goal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const storeId = authService.getCurrentStoreId() || ''

  useEffect(() => {
    if (!storeId) {
      setError('No pudimos identificar tu tienda.')
      setLoading(false)
      return
    }
    Promise.all([
      funnelService.getStoreFunnelAnalytics(storeId, 7),
      funnelService.getStoreProductsOriginsTimeline(storeId, 7).catch(() => null),
      goalService.listGoals(7).catch(() => ({ goals: [] as Goal[] })),
    ])
      .then(([funnel, tl, g]: [any, any, { goals: Goal[] }]) => {
        setTnx(funnel?.tnx_analytics || null)
        setChannelsWindow(funnel?.traffic_channels || null)
        setTimeline(tl || null)
        setGoals(g?.goals || [])
      })
      .catch((e) => setError(e?.message || 'No pudimos cargar tu embudo.'))
      .finally(() => setLoading(false))
  }, [storeId])

  const agg = useMemo(() => aggregateTimeline(timeline), [timeline])

  if (loading) return <Text className="text-center">Cargando tu centro de control…</Text>
  if (error) return <Card><Card.Body><Text className="text-center">{error}</Text></Card.Body></Card>

  // ── derive headlines from real data ──
  const channelSessions = Object.values(agg.channels).reduce((a, c) => a + c.sessions, 0)
  const coveragePct = agg.coverage.sessions_total > 0
    ? Math.round((agg.coverage.sessions_with_origin / agg.coverage.sessions_total) * 100)
    : null
  const mobile = agg.devices.mobile?.sessions || 0
  const desktop = agg.devices.desktop?.sessions || 0
  const deviceTotal = mobile + desktop + (agg.devices.unknown?.sessions || 0)
  const mobilePct = deviceTotal > 0 ? Math.round((mobile / deviceTotal) * 100) : null
  const topChannel = Object.entries(agg.channels)
    .filter(([k]) => k !== 'unattributed')
    .sort((a, b) => b[1].sessions - a[1].sessions)[0]
  const { hot, cold } = channelExtremes(agg.channels)

  // mix-guard verdict from a store-CR goal, if one exists
  const crGoal = goals.find(
    (g) => g.primary_metric?.binding?.metric === 'conversion_rate' || g.primary_metric?.binding?.kind === 'store_funnel'
  )
  const verdict = crGoal ? merchantStatus(crGoal) : null

  let acqDiagnosis: Diagnosis = null
  if (verdict?.status === 'traffic') {
    acqDiagnosis = { glyph: '⚖️', tone: 'mix', text: verdict.note || 'La caída de conversión es de tráfico, no de la tienda.' }
  } else if (cold) {
    acqDiagnosis = { glyph: '❄️', tone: 'cold', text: `${chLabel(cold.key)} trae tráfico que casi no convierte.` }
  } else if (hot) {
    acqDiagnosis = { glyph: '🔥', tone: 'hot', text: `${chLabel(hot.key)} es tu canal que mejor convierte.` }
  }

  // funnel / behavioral metrics from tnx_analytics
  const seg = tnx?.visitor_segments || null
  const bouncePct = seg?.bouncers?.percentage ?? null
  const steps = Object.values(tnx?.step_analysis || {}).sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0))
  const convStep = steps.find((s) => s.is_conversion) || steps[steps.length - 1]
  const purchases = typeof tnx?.total_conversions === 'number' ? tnx.total_conversions : convStep?.total_sessions || 0
  const preConv = steps.length >= 2 ? steps[steps.length - 2] : undefined
  const abandonPct = preConv && preConv.total_sessions > 0
    ? Math.round((1 - purchases / preConv.total_sessions) * 100)
    : null
  const topProducts = tnx?.product_analytics?.product_performance?.top_products
  const offStore = tnx?.off_storefront_orders || null

  return (
    <div className="mx-auto max-w-5xl space-y-3">
      <div className="flex items-baseline justify-between">
        <div>
          <Text fontSize="caption" fontWeight="medium" className="uppercase tracking-wide">Centro de control · Embudo</Text>
          <Text>Últimos 7 días{isMerchant ? '' : ' · vista consultor'}</Text>
        </div>
      </div>

      {/* ── FASE 1 · ADQUISICIÓN (deep reference panel) ── */}
      <PhasePanel
        name="Adquisición"
        defaultOpen={!isMerchant}
        diagnosis={acqDiagnosis}
        chips={[
          `${channelSessions.toLocaleString('es-AR')} sesiones`,
          coveragePct !== null ? `cobertura ${coveragePct}%` : '',
          mobilePct !== null ? `📱 ${mobilePct}% / 💻 ${100 - mobilePct}%` : '',
          topChannel ? `Top: ${chLabel(topChannel[0])}` : '',
        ].filter(Boolean)}
      >
        {/* Full width, like Resultados: the channel table is nine columns wide since it gained
            revenue/$-per-visit/engagement, and half a row put the last four behind a scrollbar.
            The panel body is `space-y-4`, so these stack without a wrapper. */}
        <TrafficSourceTable
          channels={channelsWindow ?? agg.channels}
          ga4ChannelTotals={timeline?.ga4_channel_totals || null}
          coverage={agg.coverage.sessions_total > 0 ? agg.coverage : null}
          breakdown={timeline?.traffic_origins_breakdown || null}
        />
        <BreakdownTable
          title="Conversión por dispositivo"
          rows={Object.entries(agg.devices).map(([key, c]) => ({
            key,
            label: key === 'mobile' ? 'Celular' : key === 'desktop' ? 'Escritorio' : 'Sin dato',
            sessions: c.sessions,
            conversions: c.conversions,
            revenue: c.revenue,
          }))}
          note="«Sin dato» = sesiones sin viewport detectado (no es un segmento accionable)."
        />
        <TrafficSourceTimeline storeId={storeId} windowDays={7} initialData={timeline} />
        <ActionsAndQuestions actions={acquisitionActions(goals)} />
      </PhasePanel>

      {/* ── FASE 2 · ENTRADA / LANDING ── */}
      <PhasePanel
        name="Entrada / Landing"
        defaultOpen={false}
        diagnosis={bouncePct !== null && bouncePct >= 50 ? { glyph: '❄️', tone: 'cold', text: `Rebote alto (${bouncePct}%) — muchos se van sin mirar nada.` } : null}
        chips={[bouncePct !== null ? `Rebote ${bouncePct}%` : 'Rebote: sin dato']}
      >
        <VisitorSegmentation segments={seg} />
        <ComingSoon what="rebote por dispositivo y por canal de entrada + acciones de landing" />
      </PhasePanel>

      {/* ── FASE 3 · EXPLORACIÓN / PRODUCTO ── */}
      <PhasePanel
        name="Exploración / Producto"
        defaultOpen={false}
        chips={[
          seg?.shoppers ? `${seg.shoppers.percentage}% miran productos` : '',
          Array.isArray(topProducts) ? `${topProducts.length} productos` : '',
        ].filter(Boolean)}
      >
        <ProductPerformanceTable tnxProducts={topProducts} />
        <ComingSoon what="calientes/fríos por categoría + acciones de merchandising" />
      </PhasePanel>

      {/* ── FASE 4 · CARRITO / CHECKOUT ── */}
      <PhasePanel
        name="Carrito / Checkout"
        defaultOpen={false}
        diagnosis={abandonPct !== null && abandonPct >= 50 ? { glyph: '❄️', tone: 'cold', text: `Se abandona el ${abandonPct}% en el último paso antes de pagar.` } : null}
        chips={[abandonPct !== null ? `Abandono ${abandonPct}%` : '', `${purchases} compras`].filter(Boolean)}
      >
        <div className="space-y-1.5">
          {steps.map((s) => (
            <div key={s.step_name} className="flex items-center justify-between text-sm">
              <Text as="span" color="neutral-textHigh">{s.step_name}</Text>
              <Text as="span" className="tabular-nums">{s.total_sessions.toLocaleString('es-AR')}</Text>
            </div>
          ))}
        </div>
        <ComingSoon what="dudas por medio de pago y fricción de envío (requiere nueva instrumentación de checkout)" />
      </PhasePanel>

      {/* ── FASE 5 · RESULTADO ── */}
      <PhasePanel
        name="Resultado"
        defaultOpen={false}
        chips={[`${purchases} ventas`, offStore && offStore.count > 0 ? `+${offStore.count} no-web` : ''].filter(Boolean)}
      >
        <RevenueAnalyticsCard analytics={tnx as RevenueAnalyticsInput} detailed={!isMerchant} />
        <OffStorefrontCard off={offStore} summary={tnx?.storefront_summary} detailed={!isMerchant} />
      </PhasePanel>
    </div>
  )
}
